import { useEffect, useState } from 'react'
import { Card, Input, SimpleGrid, Stack, Text } from '@chakra-ui/react'

import { callbackOnPressKey } from 'utils/events'
import CustomFormFieldErrorMessage from 'components/tools/CustomForm/commonComponents/CustomFormFieldErrorMessage'
import { CustomFormViewerQuestionProps } from 'components/tools/CustomForm/types/CustomFormViewerQuestionProps'
import { useCustomFormViewer } from 'components/tools/CustomForm/components/Viewer/contexts/CustomFormViewerManager/Provider'
import validateNPSOnCustomForm from 'components/tools/CustomForm/components/Viewer/utils/validateNPSOnCustomForm'
import useCustomFormGetDefaultValue from '../../hooks/useCustomFormGetDefaultValue'
import useCustomFormHandleErrorWithScrollBehavior from '../../hooks/useCustomFormHandleErrorWithScrollBehavior'

export default function CustomFormNPSQuestion({
  question,
  questionKey,
  disabled,
  formMethods,
}: CustomFormViewerQuestionProps) {
  const [selected, setSelected] = useState('')

  const { removeErrorFromQuestion, setErrorOnQuestion, thisQuestionHasError } = useCustomFormViewer()

  const { defaultValue } = useCustomFormGetDefaultValue({ question, questionKey, disabled })

  const questionWithError = thisQuestionHasError(question.slug)?.question

  const numbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']

  function handleSelectNumber(number: string) {
    if (disabled) return

    const errorMessage = validateNPSOnCustomForm({ nps: number, isRequired: question.is_required })

    if (errorMessage === '') {
      if (selected === number) {
        setSelected('')
      } else {
        setSelected(number)
      }
      removeErrorFromQuestion(question.slug)
    } else {
      setErrorOnQuestion(question, errorMessage)
    }
  }

  useEffect(() => {
    if (defaultValue) {
      setSelected(defaultValue)
    } else {
      setSelected('')
    }
  }, [defaultValue])

  useEffect(() => {
    if (question?.answer) {
      handleSelectNumber(question.answer[0])
    }
  }, [question])

  useEffect(() => {
    if (formMethods?.setValue) {
      formMethods?.setValue(questionKey, selected)
    }
  }, [selected])

  useCustomFormHandleErrorWithScrollBehavior({ questionWithError, questionSlug: question.slug })

  return (
    <Stack id={question.slug}>
      <SimpleGrid columns={5} gap={2}>
        {numbers.map((number, index) => {
          const isSelected = selected === number
          return (
            <Card
              id={`nps-${index}`}
              p={2}
              tabIndex={0}
              borderWidth="1px"
              key={number}
              className={disabled ? '' : 'accessible'}
              cursor={disabled ? 'not-allowed' : 'pointer'}
              bg={isSelected ? 'primary' : 'bgContent'}
              color={isSelected ? 'light' : 'dark'}
              onClick={() => handleSelectNumber(number)}
              onKeyUp={(event) => callbackOnPressKey(event, () => handleSelectNumber(number))}
            >
              <Text textAlign="center">{number}</Text>
            </Card>
          )
        })}

        <Input id={question.slug} isDisabled hidden />
      </SimpleGrid>

      <CustomFormFieldErrorMessage message={questionWithError?.error_message} />
    </Stack>
  )
}
